import { alpha, Avatar, Box, styled } from '@mui/material';
import Highlighter from 'react-highlight-words';
import React, { useState } from 'react';

import { PatientAutocompleteOption } from './NavBar.types';
import { useHackathonToggle } from './HackathonToggles';

const HACKATHON_AVATARS = ['homer1.png', 'homer2.png', 'ashlee.png', 'homie.png', 'homey.png', 'dude.png'];

function getAvatarUrl(id?: number): string {
  const index = (id ?? 0) % HACKATHON_AVATARS.length;
  return `/hackathon-avatars/${HACKATHON_AVATARS[index]}`;
}

const classes = {
  searchName: 'searchName',
  searchDescription: 'searchDescription',
  searchMeta: 'searchMeta',
  searchHighlightName: 'searchHighlightName',
  searchHighlightDescription: 'searchHighlightDescription',
  emailMore: 'emailMore',
};

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
  [`& .${classes.searchName}`]: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
  },

  [`& .${classes.searchDescription}`]: {
    fontSize: theme.typography.body2.fontSize,
  },

  [`& .${classes.searchMeta}`]: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
  },

  [`& .${classes.searchHighlightName}`]: {
    backgroundColor: alpha(theme.palette.info.light, 0.1),
    color: theme.palette.info.dark,
  },

  [`& .${classes.searchHighlightDescription}`]: {
    backgroundColor: 'transparent',
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.primary.light,
  },

  [`& .${classes.emailMore}`]: {
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightMedium,
    textDecoration: 'underline',
    '&:hover': { opacity: 0.7 },
  },
}));

function EmailSummary({ emails, searchQuery }: { emails: string[]; searchQuery: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!emails.length) return null;

  if (expanded) {
    return (
      <span>
        <Highlighter
          className={classes.searchDescription}
          highlightClassName={classes.searchHighlightDescription}
          searchWords={[searchQuery]}
          autoEscape
          textToHighlight={emails.join(', ')}
        />
        {emails.length > 1 && (
          <>
            <span className={classes.searchDescription}> </span>
            <span
              className={`${classes.searchDescription} ${classes.emailMore}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); setExpanded(false); } }}
              role="button"
              tabIndex={0}
            >
              show less
            </span>
          </>
        )}
      </span>
    );
  }

  const rest = emails.length - 1;
  return (
    <span>
      <Highlighter
        className={classes.searchDescription}
        highlightClassName={classes.searchHighlightDescription}
        searchWords={[searchQuery]}
        autoEscape
        textToHighlight={emails[0]}
      />
      {rest > 0 && (
        <>
          <span className={classes.searchDescription}> </span>
          <span
            className={`${classes.searchDescription} ${classes.emailMore}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(true); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); setExpanded(true); } }}
            role="button"
            tabIndex={0}
          >
            {`+${rest} more`}
          </span>
        </>
      )}
    </span>
  );
}

function PatientSearchResult({
  option,
  searchQuery,
}: {
  option: PatientAutocompleteOption;
  searchQuery: string;
}) {
  const hack = {
    showServiceArea: useHackathonToggle('showServiceArea'),
    showPcp: useHackathonToggle('showPcp'),
    showProfilePhoto: useHackathonToggle('showProfilePhoto'),
    collapseEmails: useHackathonToggle('collapseEmails'),
  };

  if (option.title === 'View All Results') {
    return (
      <Root>
        <Box color="primary.main" fontWeight="bold">
          View All Results
        </Box>
      </Root>
    );
  }
  return (
    <Root data-cy="patient-search-row">
      <Box display="flex" alignItems="center" gap={1.5}>
        {hack.showProfilePhoto && (
          <Avatar
            src={getAvatarUrl(option.id)}
            sx={{ width: 36, height: 36, flexShrink: 0 }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <Highlighter
            className={classes.searchName}
            highlightClassName={classes.searchHighlightName}
            searchWords={[searchQuery]}
            autoEscape
            textToHighlight={option.title}
          />
          {option.description && (
            <div>
              <Highlighter
                className={classes.searchDescription}
                highlightClassName={classes.searchHighlightDescription}
                searchWords={[searchQuery]}
                autoEscape
                textToHighlight={option.description}
              />
              {hack.collapseEmails && option.emails && option.emails.length > 0 && (
                <>
                  <span className={classes.searchDescription}> · </span>
                  <EmailSummary emails={option.emails} searchQuery={searchQuery} />
                </>
              )}
              {!hack.collapseEmails && option.emails && option.emails.length > 0 && (
                <Highlighter
                  className={classes.searchDescription}
                  highlightClassName={classes.searchHighlightDescription}
                  searchWords={[searchQuery]}
                  autoEscape
                  textToHighlight={` · ${option.emails.join(', ')}`}
                />
              )}
            </div>
          )}
          {(option.serviceAreaName || option.pcpName) && (
            <div className={classes.searchMeta}>
              {[
                hack.showServiceArea && option.serviceAreaName,
                hack.showPcp && option.pcpName && `PCP: ${option.pcpName}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
      </Box>
    </Root>
  );
}

export default PatientSearchResult;
